/**
 * Alpine Slider Component
 *
 * Hybrid approach: Alpine.js for reactive state + CSS scroll-snap for mechanics.
 * Replaces the store-slider web component with a simpler, more reliable solution.
 *
 * Usage:
 *   <div x-data="slider({ autoplay: true, autoplayInterval: 5000 })" ...>
 *     <div x-ref="track" @scroll.passive="onScroll()" class="store-carousel ...">
 *       <!-- slides -->
 *     </div>
 *   </div>
 *
 * Features:
 * - Reactive state (currentIndex, slideCount, pages)
 * - Recounts when slides are added/removed under a live component (MutationObserver)
 * - Optional mouse drag gesture: `slider({ drag: true, dragRatio: 0.2 })` or
 *   `data-drag` on the root — slides stay static, moving 20% of the track width
 *   fires next()/prev()
 * - Autoplay with pause on hover/focus/touch; starts only after window load
 * - Reliable initial position via $nextTick
 * - Thumbnail slider sync (leader/follower pattern)
 * - Support for full-bleed/auto-offset edge alignment
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('slider', (config = {}) => ({
    // === State ===
    currentIndex: 0,
    slideCount: 0,
    visibleCount: 1,
    isAutoplayPaused: false,
    initialized: false,

    // === Config (from x-data) ===
    id: config.id || '',
    autoplay: config.autoplay ?? false,
    autoplayInterval: config.autoplayInterval ?? 5000,
    followerId: config.followerId || '', // data-slider-id for thumbnail sync

    // === Internal ===
    _autoplayTimer: null,
    _resizeObserver: null,
    // Scroll-settle idle gate. Reset on every scroll event AND on every
    // scrollToIndex call, so the observer only reconciles state once the
    // scroll has truly settled. Cancelling on scrollToIndex eliminates the
    // stale-timer race: a previous transition's pending detect cannot fire
    // mid-way through a fresh navigation and overwrite the optimistic dot.
    _scrollIdleTimer: null,
    // Burst-click idle gate for centering thumbnail/follower tracks. Reset on
    // every scrollToIndex so a flurry of arrow/thumb clicks only triggers ONE
    // follower smooth-scroll at the final target. Dot state updates per click.
    _followerScrollTimer: null,

    // === Lifecycle ===
    init() {
      // Opt-in: when the slider declares `data-variant-reactive`, its slides are
      // variant-gated (x-show), so some are display:none at any moment. Count only
      // VISIBLE children, and recount when the selected variant changes.
      this._variantReactive = this.$el.hasAttribute('data-variant-reactive');
      // Opt-in mouse drag (touch already scrolls natively): `slider({ drag: true })`
      // or `data-drag` on the root. `dragRatio` = share of the track width the
      // pointer must travel before the gesture fires next()/prev().
      this._drag = config.drag ?? this.$el.hasAttribute('data-drag');
      this.dragRatio = config.dragRatio ?? 0.2;

      // Wait for DOM to settle before calculating
      this.$nextTick(() => {
        this._calculateSlides();
        this._setupResizeObserver();
        this._setupChildObserver();
        if (this._drag) this._setupDrag();

        // Ensure first slide is visible (fix for video slider bug)
        // Use double $nextTick to ensure videos/images have rendered
        this.$nextTick(() => {
          this.scrollToIndex(0, 'auto');

          // Mark initialized after initial scroll
          requestAnimationFrame(() => {
            this.initialized = true;
          });
        });

        // Start autoplay if enabled
        if (this.autoplay) {
          this._startAutoplayAfterLoad();
        }

        // Listen for follower clicks (thumbnail sync)
        this._setupFollowerSync();
      });

      // Variant-reactive sliders recount when the selected variant changes (the
      // set of visible slides changes), then snap back to the first slide.
      if (this._variantReactive) {
        this._onVariantChange = () => this.$nextTick(() => {
          this._calculateSlides();
          this.scrollToIndex(0, 'auto');
          this._updateFollowers();
        });
        window.addEventListener('store:variant-changed', this._onVariantChange);
      }
    },

    destroy() {
      this._stopAutoplay();
      this._cancelTween(this._getTrack());
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      if (this._childObserver) {
        this._childObserver.disconnect();
        this._childObserver = null;
      }
      if (this._teardownDrag) {
        this._teardownDrag();
        this._teardownDrag = null;
      }
      if (this._scrollIdleTimer) {
        clearTimeout(this._scrollIdleTimer);
      }
      if (this._followerScrollTimer) {
        clearTimeout(this._followerScrollTimer);
      }
      if (this._onVariantChange) {
        window.removeEventListener('store:variant-changed', this._onVariantChange);
        this._onVariantChange = null;
      }
    },

    // === Computed ===
    get pages() {
      if (this.visibleCount >= this.slideCount) return 1;
      // One dot per full-viewport page. A trailing partial page still gets its own
      // dot; that dot is reachable because `currentPage` snaps to the last page once
      // the track hits maxIndex (see below) and `scrollToPage` clamps the final
      // page's start to maxIndex. This keeps page-jump navigation while still
      // revealing trailing cards (e.g. 8 cards / 5 per view = 2 pages, page 2 = 4–8).
      return Math.ceil(this.slideCount / this.visibleCount);
    },

    get currentPage() {
      // At the end of the track, snap to the final page so the last dot activates
      // even when the trailing page is partial (maxIndex < (pages-1)*visibleCount).
      if (this.currentIndex >= this.maxIndex) return Math.max(0, this.pages - 1);
      return Math.floor(this.currentIndex / Math.max(1, this.visibleCount));
    },

    get canPrev() {
      return this.currentIndex > 0;
    },

    get canNext() {
      return this.currentIndex < this.slideCount - this.visibleCount;
    },

    get maxIndex() {
      return Math.max(0, this.slideCount - this.visibleCount);
    },

    // === Private Methods ===
    // Get track element - either $refs.track or $el itself (when x-data is on the scrollable element)
    _getTrack() {
      return this.$refs.track || this.$el;
    },

    // Element children that count as slides. Variant-reactive sliders exclude
    // hidden (display:none) children so x-show-gated slides don't inflate the
    // count; falls back to all children if none are currently visible.
    _slideEls() {
      const track = this._getTrack();
      if (!track) return [];
      const all = Array.from(track.children).filter((el) => el.nodeType === 1);
      // Exclude hidden (display:none) children — e.g. variant-gated slides — so the
      // index↔position mapping used by measure/navigate/detect stays aligned to what's
      // actually shown. Sliders whose children are all visible are unaffected
      // (visible === all). Fall back to all if nothing is currently visible (e.g. the
      // slider is measured while its section is collapsed).
      const visible = all.filter((el) => el.offsetParent !== null);
      return visible.length ? visible : all;
    },

    _calculateSlides() {
      const track = this._getTrack();
      if (!track) return;

      const children = this._slideEls();
      this.slideCount = children.length;

      if (!children.length) {
        this.visibleCount = 1;
        return;
      }

      const firstSlide = children[0];
      const slideWidth = firstSlide.offsetWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const containerWidth = track.clientWidth;

      if (slideWidth <= 0) {
        this.visibleCount = 1;
        return;
      }

      // Calculate how many slides fit. +0.02 tolerance so a card that is ~98%+
      // visible counts as fully visible — avoids an off-by-one when N cards fit to
      // within a sub-pixel rounding hair (e.g. 4.996 should be 5, not 4).
      const stride = slideWidth + gap;
      this.visibleCount = Math.max(1, Math.floor((containerWidth + gap) / stride + 0.02));
    },

    _setupResizeObserver() {
      const track = this._getTrack();
      if (!track || !('ResizeObserver' in window)) return;

      this._resizeObserver = new ResizeObserver(() => {
        this._calculateSlides();
      });
      this._resizeObserver.observe(track);
    },

    // Recount when slides are added/removed underneath a live component — e.g.
    // the cart upsell strip, whose cells are re-rendered by the section morph
    // while this x-data root survives. Debounced to one recount per tick.
    _setupChildObserver() {
      const track = this._getTrack();
      if (!track || !('MutationObserver' in window)) return;

      this._childObserver = new MutationObserver(() => {
        if (this._childRecountQueued) return;
        this._childRecountQueued = true;
        this.$nextTick(() => {
          this._childRecountQueued = false;
          this._calculateSlides();
          this._detectActiveIndex();
        });
      });
      this._childObserver.observe(track, { childList: true });
    },

    // Mouse drag on the track — a gesture, not a scroll. Pointer events only
    // for `mouse`; touch keeps the native scroll-snap swipe. The slides stay
    // STATIC while the pointer moves; once the pointer has travelled
    // `dragRatio` of the track width (default 20%, min 24px) the gesture fires
    // the same action as the arrows (`next()` / `prev()`, one page) and the rest
    // of the gesture is ignored. Any move past the click tolerance swallows the
    // click that ends the gesture so an "Add" button under the pointer doesn't
    // fire.
    _setupDrag() {
      const track = this._getTrack();
      if (!track) return;
      // A drag that can't scroll (all slides fit) would otherwise select text.
      track.style.userSelect = 'none';
      const CLICK_TOLERANCE = 4;
      let active = false;
      let fired = false;
      let swallowClick = false;
      let startX = 0;

      const threshold = () => Math.max(24, track.clientWidth * this.dragRatio);

      const onDown = (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        active = true;
        fired = false;
        startX = e.clientX;
      };
      const onMove = (e) => {
        if (!active || fired) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) < CLICK_TOLERANCE) return;
        e.preventDefault();
        swallowClick = true;
        track.classList.add('is-dragging');
        if (Math.abs(dx) < threshold()) return;
        fired = true;
        track.classList.remove('is-dragging');
        if (dx < 0) this.next(); else this.prev();
      };
      const onUp = () => {
        if (!active) return;
        active = false;
        track.classList.remove('is-dragging');
        // Clear the swallow after the click this pointerup dispatches.
        if (swallowClick) setTimeout(() => { swallowClick = false; }, 0);
      };
      const onClick = (e) => {
        if (!swallowClick) return;
        e.preventDefault();
        e.stopPropagation();
      };
      const onDragStart = (e) => e.preventDefault(); // no native image/link drag

      track.addEventListener('pointerdown', onDown);
      track.addEventListener('pointermove', onMove);
      track.addEventListener('pointerup', onUp);
      track.addEventListener('pointercancel', onUp);
      track.addEventListener('click', onClick, true);
      track.addEventListener('dragstart', onDragStart);
      this._teardownDrag = () => {
        track.removeEventListener('pointerdown', onDown);
        track.removeEventListener('pointermove', onMove);
        track.removeEventListener('pointerup', onUp);
        track.removeEventListener('pointercancel', onUp);
        track.removeEventListener('click', onClick, true);
        track.removeEventListener('dragstart', onDragStart);
      };
    },

    _setupFollowerSync() {
      // If this slider has an ID, listen for clicks on followers (thumbnails)
      if (!this.id) return;

      const section = this.$el.closest('[id^="shopify-section-"]') || document;

      section.addEventListener('click', (e) => {
        const dot = e.target.closest(`[data-slider-id="${this.id}"] [data-slide-index]`);
        if (dot) {
          const index = parseInt(dot.getAttribute('data-slide-index'), 10);
          if (!Number.isNaN(index)) {
            this.goToSlide(index);
          }
        }
      });
    },

    _getPaddingLeft() {
      const track = this.$refs.track;
      if (!track) return 0;
      return parseFloat(getComputedStyle(track).paddingLeft) || 0;
    },

    _getSlideLeft(index) {
      const track = this.$refs.track;
      if (!track) return 0;

      const children = this._slideEls();
      const slide = children[index];
      if (!slide) return 0;

      const padLeft = this._getPaddingLeft();
      return Math.max(0, slide.offsetLeft - padLeft);
    },

    _getPageStartLeft(page) {
      const startIndex = Math.min(page * this.visibleCount, this.maxIndex);
      return this._getSlideLeft(startIndex);
    },

    // === Navigation Methods ===
    //
    // Mental model: scroll position is the single source of truth. Click
    // handlers set state OPTIMISTICALLY (so the active dot flips instantly),
    // then issue a smooth-scroll. The `onScroll` observer eventually
    // reconciles `currentIndex` to whatever slide the scroll actually landed
    // on. If the browser's snap or animation lands somewhere unexpected, the
    // observer wins — keeping the dot in lockstep with the visible slide.
    //
    // No `isNavigating` flag, no snap-toggle: the scroll-settle idle timer is
    // cancelled on every `scrollToIndex` so stale observations from a prior
    // transition cannot overwrite a fresh click.
    scrollToIndex(index, behavior = 'smooth') {
      const track = this.$refs.track;
      if (!track) return;

      const clampedIndex = Math.max(0, Math.min(index, this.maxIndex));
      const left = this._getSlideLeft(clampedIndex);

      // Optimistic: dot updates immediately for instant click feedback.
      this.currentIndex = clampedIndex;
      this._updateFollowerDots();

      // Debounced follower-track centering. Burst clicks reset the timer so
      // only the final target runs through one clean smooth-scroll on the
      // thumbnail strip.
      if (this._followerScrollTimer) clearTimeout(this._followerScrollTimer);
      this._followerScrollTimer = setTimeout(() => {
        this._followerScrollTimer = null;
        this._centerFollowerTracks();
      }, 100);

      // Cancel any pending scroll-settle observer from a previous transition.
      // A fresh navigation overrides the previous scroll's reconciliation.
      if (this._scrollIdleTimer) clearTimeout(this._scrollIdleTimer);
      this._scrollIdleTimer = null;

      if (behavior === 'smooth') {
        this._tweenScrollTo(track, left);
      } else {
        this._cancelTween(track);
        track.scrollTo({ left, behavior });
      }
    },

    // === Eased travel ===
    //
    // Native `scrollTo({behavior:'smooth'})` runs on a browser-owned curve that
    // differs per engine and can't consume the theme's motion tokens — the
    // reason slider travel used to feel rough. Instead, tween scrollLeft
    // through --ease-out-expo over --motion-travel (the travel token: longer
    // than surface, because the expo curve front-loads velocity and reads too
    // quick over real distance). Scroll-snap is disabled only while the tween
    // runs (some engines force programmatic positions onto snap points
    // mid-flight) and restored at the exact target, so there is no settle
    // jump. Touch swipes never enter this path — they keep native scroll
    // physics — and reduced-motion users get an instant jump.
    _motionMs() {
      try {
        const v = getComputedStyle(document.documentElement).getPropertyValue('--motion-travel').trim();
        if (v.endsWith('ms')) return parseFloat(v);
        if (v.endsWith('s')) return parseFloat(v) * 1000;
      } catch (_) {}
      return 800;
    },
    _cancelTween(track) {
      if (this._tweenRaf) { cancelAnimationFrame(this._tweenRaf); this._tweenRaf = null; }
      if (track && this._tweenSnapDisabled) {
        track.style.scrollSnapType = '';
        this._tweenSnapDisabled = false;
      }
    },
    _tweenScrollTo(track, left) {
      this._cancelTween(track);
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duration = this._motionMs();
      if (reduce || !(duration > 0)) { track.scrollTo({ left }); return; }
      const from = track.scrollLeft;
      const delta = left - from;
      if (Math.abs(delta) < 1) return;
      track.style.scrollSnapType = 'none';
      this._tweenSnapDisabled = true;
      // A user gesture takes over instantly: drop the tween, restore snap.
      const abort = () => this._cancelTween(track);
      track.addEventListener('wheel', abort, { once: true, passive: true });
      track.addEventListener('touchstart', abort, { once: true, passive: true });
      const start = performance.now();
      const easeOutExpo = (t) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        track.scrollLeft = from + delta * easeOutExpo(t);
        if (t < 1) {
          this._tweenRaf = requestAnimationFrame(step);
        } else {
          this._tweenRaf = null;
          track.style.scrollSnapType = '';
          this._tweenSnapDisabled = false;
          track.removeEventListener('wheel', abort);
          track.removeEventListener('touchstart', abort);
        }
      };
      this._tweenRaf = requestAnimationFrame(step);
    },

    scrollToPage(page, behavior = 'smooth') {
      const clampedPage = Math.max(0, Math.min(page, this.pages - 1));
      const startIndex = Math.min(clampedPage * this.visibleCount, this.maxIndex);
      this.scrollToIndex(startIndex, behavior);
    },

    next() {
      if (!this.canNext) {
        // Loop to start if at end
        if (this.autoplay) {
          this.scrollToIndex(0);
        }
        return;
      }
      // Step by PAGE, not raw index. The last page snaps to maxIndex (which may not
      // be a multiple of visibleCount), so index-based stepping desyncs from the page
      // dots — prev() from the snapped last page would skip a page. scrollToPage
      // clamps the final page's start to maxIndex, keeping arrows and dots in lockstep.
      this.scrollToPage(this.currentPage + 1);
      this._restartAutoplay();
    },

    prev() {
      if (!this.canPrev) return;
      // Step by PAGE (see next()): currentPage-1 lands on the correct previous page
      // even from the snapped last page, instead of an off-grid raw index.
      this.scrollToPage(this.currentPage - 1);
      this._restartAutoplay();
    },

    goToPage(page) {
      this.scrollToPage(page);
      this._restartAutoplay();
    },

    goToSlide(index) {
      this.scrollToIndex(index);
      this._restartAutoplay();
    },

    // === Scroll Detection ===
    onScroll() {
      // Single source of truth: scroll position. No navigation gate — the
      // observer treats programmatic and user-driven scrolls identically.
      // The idle timer is reset on every scroll event AND on every
      // `scrollToIndex` (see comment there), so a fresh click overrides any
      // pending observation from the previous scroll.
      //
      // Native scroll fires ~60×/sec during a finger drag + momentum tail.
      // The 120 ms debounce ensures we only reconcile once the scroll has
      // truly settled — preventing _detectActiveIndex oscillation around
      // snap-point boundaries.
      if (this._scrollIdleTimer) clearTimeout(this._scrollIdleTimer);
      this._scrollIdleTimer = setTimeout(() => {
        this._scrollIdleTimer = null;
        this._detectActiveIndex();
        this._updateFollowers();
      }, 120);
    },

    _detectActiveIndex() {
      const track = this.$refs.track;
      if (!track) return;

      const children = this._slideEls();
      if (!children.length) return;

      const scrollLeft = track.scrollLeft;
      const padLeft = this._getPaddingLeft();

      let closest = 0;
      let minDelta = Infinity;

      for (let i = 0; i < children.length; i++) {
        const slideLeft = children[i].offsetLeft - padLeft;
        const delta = Math.abs(slideLeft - scrollLeft);
        if (delta < minDelta) {
          minDelta = delta;
          closest = i;
        }
      }

      this.currentIndex = closest;
    },

    _updateFollowers() {
      // Combined update — used by initial scroll, onScroll settle, and any
      // call site that wants both dot state AND follower scroll in one go.
      this._updateFollowerDots();
      this._centerFollowerTracks();
    },

    _updateFollowerDots() {
      // State-only update: is-selected class, aria-current, slider:change
      // event. Safe to call on every arrow click without causing visual jank
      // because it doesn't touch the follower scroll position.
      if (!this.id) return;

      const section = this.$el.closest('[id^="shopify-section-"]') || document;

      const dots = section.querySelectorAll(`[data-slider-id="${this.id}"] .store-dot, .store-slider-pagination[data-slider-id="${this.id}"] .store-dot`);
      dots.forEach((dot, i) => {
        const isPage = dot.hasAttribute('data-page-index');
        const isActive = isPage
          ? this.currentPage === i
          : this.currentIndex === i;

        dot.classList.toggle('is-selected', isActive);
        dot.classList.toggle('is-active', isActive);

        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }

        if (!isPage) {
          dot.setAttribute('data-slide-index', String(i));
        }
      });

      this.$el.dispatchEvent(new window.Theme.events.SliderChangeEvent({
        index: this.currentIndex,
        page: this.currentPage,
        id: this.id
      }));
    },

    _centerFollowerTracks() {
      // Scroll follower tracks (thumbnails) so the active dot is centred in
      // their viewport. Centering scrolls symmetrically: stepping forward
      // reveals upcoming thumbs to the right; stepping backward reveals
      // earlier thumbs to the left. Browser clamps scroll to
      // [0, scrollWidth - clientWidth].
      if (!this.id) return;

      const section = this.$el.closest('[id^="shopify-section-"]') || document;

      const followerTracks = section.querySelectorAll(`[data-slider-id="${this.id}"].store-carousel, store-slider[data-slider-id="${this.id}"]`);
      followerTracks.forEach(follower => {
        const followerDots = follower.querySelectorAll('.store-dot');
        const activeDot = followerDots[this.currentIndex];
        if (activeDot && follower.scrollWidth > follower.clientWidth) {
          const dotCenter = activeDot.offsetLeft + (activeDot.offsetWidth / 2);
          const targetLeft = Math.max(0, dotCenter - (follower.clientWidth / 2));
          follower.scrollTo({ left: targetLeft, behavior: 'smooth' });
        }
      });
    },

    // === Autoplay ===
    // First advance waits for window load. A slide whose image is still downloading
    // when autoplay brings it into view paints for the first time on screen and
    // becomes a new, late LCP candidate. Background images hold the load event, so
    // after load every slide has already painted off-screen.
    _startAutoplayAfterLoad() {
      if (document.readyState === 'complete') {
        this._startAutoplay();
        return;
      }
      window.addEventListener('load', () => this._startAutoplay(), { once: true });
    },

    _startAutoplay() {
      if (!this.autoplay || this.isAutoplayPaused) return;
      if (this.pages <= 1) return;
      // Ambient tier: never auto-advance for reduced-motion users.
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this._stopAutoplay();

      this._autoplayTimer = setInterval(() => {
        if (!this.isAutoplayPaused) {
          this.next();
        }
      }, this.autoplayInterval);
    },

    _stopAutoplay() {
      if (this._autoplayTimer) {
        clearInterval(this._autoplayTimer);
        this._autoplayTimer = null;
      }
    },

    _restartAutoplay() {
      if (this.autoplay && !this.isAutoplayPaused) {
        this._startAutoplay();
      }
    },

    pauseAutoplay() {
      this.isAutoplayPaused = true;
      this._stopAutoplay();
    },

    resumeAutoplay() {
      this.isAutoplayPaused = false;
      if (this.autoplay) {
        this._startAutoplay();
      }
    },

    // === Control Handlers (for external buttons) ===
    handlePrev() {
      this.prev();
    },

    handleNext() {
      this.next();
    },

    handleDotClick(index) {
      this.goToSlide(index);
    },

    handlePageClick(page) {
      this.goToPage(page);
    }
  }));
});

/**
 * Global slider control handler
 * Handles clicks on slider controls that are outside the Alpine scope.
 * Looks for nearest slider in the section and calls its methods.
 */
document.addEventListener('click', (e) => {
  const control = e.target.closest('[data-action="prev"], [data-action="next"]');
  if (!control) return;

  const wrapper = control.closest('.store-slider-controls');
  if (!wrapper) return;

  const sliderId = wrapper.getAttribute('data-slider-id');
  const section = wrapper.closest('[id^="shopify-section-"]') || document;

  let slider = null;

  if (sliderId) {
    slider = section.querySelector(`#${CSS.escape(sliderId)}`);
  }

  // Fallback: find nearest slider in section
  if (!slider) {
    slider = section.querySelector('[x-data*="slider"]');
  }

  if (!slider || !slider._x_dataStack) return;

  const alpineData = slider._x_dataStack[0];
  if (!alpineData) return;

  const action = control.getAttribute('data-action');
  if (action === 'prev' && typeof alpineData.prev === 'function') {
    alpineData.prev();
  } else if (action === 'next' && typeof alpineData.next === 'function') {
    alpineData.next();
  }
});

/**
 * Global pagination click handler
 */
document.addEventListener('click', (e) => {
  const dot = e.target.closest('.store-dot[data-page-index], .store-dot[data-slide-index]');
  if (!dot) return;

  const wrapper = dot.closest('.store-slider-pagination, .store-pagination');
  if (!wrapper) return;

  const sliderId = wrapper.getAttribute('data-slider-id') ||
                   wrapper.closest('[data-slider-id]')?.getAttribute('data-slider-id');
  const section = wrapper.closest('[id^="shopify-section-"]') || document;

  let slider = null;

  if (sliderId) {
    slider = section.querySelector(`#${CSS.escape(sliderId)}`);
  }

  // Fallback: find nearest slider in section
  if (!slider) {
    slider = section.querySelector('[x-data*="slider"]');
  }

  if (!slider || !slider._x_dataStack) return;

  const alpineData = slider._x_dataStack[0];
  if (!alpineData) return;

  const pageIndex = dot.getAttribute('data-page-index');
  const slideIndex = dot.getAttribute('data-slide-index');

  if (pageIndex !== null && typeof alpineData.goToPage === 'function') {
    alpineData.goToPage(parseInt(pageIndex, 10));
  } else if (slideIndex !== null && typeof alpineData.goToSlide === 'function') {
    alpineData.goToSlide(parseInt(slideIndex, 10));
  }
});

/**
 * Alpine Slider Pagination Component
 *
 * Syncs with a slider component to display navigation dots.
 * Dynamically creates dots based on slide/page count.
 *
 * Usage:
 *   <div x-data="sliderPagination({ sliderId: 'my-slider', mode: 'slides' })">
 *     <template x-for="(item, i) in items">
 *       <button @click="goTo(i)" :class="currentIndex === i ? 'active' : ''"></button>
 *     </template>
 *   </div>
 */
document.addEventListener('alpine:init', () => {
  Alpine.data('sliderPagination', (config = {}) => ({
    // === State ===
    items: [],
    currentIndex: 0,

    // === Config ===
    sliderId: config.sliderId || '',
    mode: config.mode || 'slides', // 'slides' or 'pages'

    // === Internal ===
    _slider: null,
    _resizeObserver: null,

    // === Lifecycle ===
    init() {
      this.$nextTick(() => {
        this._findSlider();
        this._syncState();
        this._setupListeners();
        this._setupResizeObserver();
      });
    },

    destroy() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    },

    // === Private Methods ===
    _findSlider() {
      const section = this.$el.closest('[id^="shopify-section-"]') || document;

      if (this.sliderId) {
        this._slider = section.querySelector(`#${CSS.escape(this.sliderId)}`);
      }

      // Fallback: nearest slider in section
      if (!this._slider) {
        this._slider = section.querySelector('[x-data*="slider"]');
      }
    },

    _getSliderData() {
      if (!this._slider || !this._slider._x_dataStack) return null;
      return this._slider._x_dataStack[0];
    },

    _syncState() {
      const data = this._getSliderData();
      if (!data) {
        // Retry after Alpine initializes
        setTimeout(() => this._syncState(), 100);
        return;
      }

      const count = this.mode === 'pages' ? data.pages : data.slideCount;
      const index = this.mode === 'pages' ? data.currentPage : data.currentIndex;

      // Create items array for x-for
      this.items = Array.from({ length: count || 0 }, (_, i) => i);
      this.currentIndex = index || 0;
    },

    _setupListeners() {
      if (!this._slider) return;

      // Listen for slider:change custom event
      this._slider.addEventListener('slider:change', (e) => {
        // Refresh dot count when the slide/page count changes (e.g. a
        // variant-reactive slider swapped to a set of a different length).
        const data = this._getSliderData();
        if (data) {
          const count = this.mode === 'pages' ? data.pages : data.slideCount;
          if (count !== this.items.length) {
            this.items = Array.from({ length: count || 0 }, (_, i) => i);
          }
        }
        const { index, page } = e.detail;
        this.currentIndex = this.mode === 'pages' ? page : index;
      });
    },

    _setupResizeObserver() {
      if (!this._slider || !('ResizeObserver' in window)) return;

      // Re-sync on resize (slide count may change)
      this._resizeObserver = new ResizeObserver(() => {
        this._syncState();
      });
      this._resizeObserver.observe(this._slider);
    },

    // === Public Methods ===
    goTo(index) {
      const data = this._getSliderData();
      if (!data) return;

      if (this.mode === 'pages' && typeof data.goToPage === 'function') {
        data.goToPage(index);
      } else if (typeof data.goToSlide === 'function') {
        data.goToSlide(index);
      }

      this.currentIndex = index;
    }
  }));
});
